import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { formatBRL, formatPct } from "../../utils/format";
import { FlaskConical, Package, Sparkles, Search, Plus, X, AlertTriangle, Info, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Painel de seleção + otimização de Avaliações Químicas para o wizard de propostas.
 *
 * Props:
 *   - value: array de ChemicalEvalItem (controlled)
 *   - onChange: (newItems) => void
 *   - forceNonOptimized: boolean global (P0 user pref)
 *   - onToggleForce: (bool) => void
 */
export default function ChemicalOptimizerPanel({
  value = [],
  onChange,
  selectedNames,
  onSelectedChange,
  forceNonOptimized,
  onToggleForce,
}) {
  const { isAdmin } = useAuth();
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [coletores, setColetores] = useState([]);
  const [config, setConfig] = useState(null);
  const [q, setQ] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const [result, setResult] = useState(null);
  const [subsetPasivo, setSubsetPassivo] = useState([]);

  // Controlled from parent when selectedNames is supplied; otherwise derive from value
  const controlled = Array.isArray(selectedNames);
  const [selectedLocal, setSelectedLocal] = useState(() => {
    const s = new Set();
    (value || []).forEach((v) => { if (v.origem_nome) s.add(v.origem_nome); });
    return s;
  });
  const selected = controlled ? new Set(selectedNames) : selectedLocal;
  const updateSelected = (setter) => {
    const next = setter(selected);
    if (controlled) onSelectedChange(Array.from(next));
    else setSelectedLocal(next);
  };

  useEffect(() => {
    (async () => {
      try {
        const [a, p, c, cfg] = await Promise.all([
          api.get("/chem-catalog/avaliacoes"),
          api.get("/chem-catalog/pacotes"),
          api.get("/chem-catalog/coletores"),
          api.get("/chem-catalog/config"),
        ]);
        setAvaliacoes(a.data || []);
        setPacotes(p.data || []);
        setColetores(c.data || []);
        setConfig(cfg.data);
      } catch {
        toast.error("Falha ao carregar catálogo químico");
      }
    })();
  }, []);

  const markup = config?.multiplicador_markup_default ?? 1.75;
  const transporte = config?.custo_transporte_default_brl ?? 45.0;

  const coletorByCode = useMemo(
    () => Object.fromEntries(coletores.map((c) => [c.codigo_lab, c])),
    [coletores]
  );

  const avaliacoesAtivas = useMemo(() => avaliacoes.filter((a) => a.ativo !== false), [avaliacoes]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return avaliacoesAtivas;
    return avaliacoesAtivas.filter(
      (a) =>
        (a.nome || "").toLowerCase().includes(needle) ||
        (a.cas || "").toLowerCase().includes(needle)
    );
  }, [avaliacoesAtivas, q]);

  const toggleSelected = (nome) => {
    updateSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nome)) next.delete(nome);
      else next.add(nome);
      return next;
    });
  };

  const clearAll = () => updateSelected(() => new Set());

  // Stable string key for the current selection (used as effect dep to avoid identity churn).
  const selectedKey = useMemo(() => Array.from(selected).sort().join("|"), [selected]);

  const optimize = useCallback(async () => {
    const names = Array.from(selected);
    if (names.length === 0) {
      setResult(null);
      onChange([]);
      setSubsetPassivo([]);
      return;
    }
    setOptimizing(true);
    try {
      const r = await api.post("/chem-catalog/otimizar", {
        avaliacoes_solicitadas: names,
        otimizar: !forceNonOptimized,
      });
      setResult(r.data);

      // Build ChemicalEvalItems from optimizer result
      const newItems = [];
      const mkVenda = (custo, mult) => +(custo * (mult || markup)).toFixed(2);

      // Packages
      (r.data.pacotes_aplicados || []).forEach((p) => {
        const pac = pacotes.find((x) => x.nome === p.nome) || {};
        const custo = (p.custo_brl || 0) + transporte;
        newItems.push({
          chemical_id: `pkg:${p.nome}`,
          nome: `[Pacote] ${p.nome} — ${pac.descricao || ""}`.trim(),
          status: "included",
          litragem_escolhida: 0,
          tempo_min: 0,
          custo,
          venda: mkVenda(custo, markup),
          origem_tipo: "pacote",
          origem_nome: p.nome,
          cobre: p.cobre || [],
        });
      });

      // Individuals
      (r.data.individuais_aplicados || []).forEach((ind) => {
        const av = avaliacoes.find((x) => x.nome === ind.nome) || {};
        const custo = (ind.custo_brl || 0) + (av.custo_insumos_extra_brl || 0) + transporte;
        newItems.push({
          chemical_id: `av:${ind.nome}`,
          nome: ind.nome,
          status: "included",
          litragem_escolhida: 0,
          tempo_min: 0,
          custo,
          venda: mkVenda(custo, av.multiplicador_custom || markup),
          origem_tipo: "individual",
          origem_nome: ind.nome,
        });
      });

      // Subcontratados (preço = 0, obs marca como "sob cotação")
      (r.data.subcontratados || []).forEach((nome) => {
        newItems.push({
          chemical_id: `sub:${nome}`,
          nome: `${nome} (sob cotação — lab subcontratado)`,
          status: "not_included",
          litragem_escolhida: 0,
          tempo_min: 0,
          custo: 0,
          venda: 0,
          origem_tipo: "subcontratado",
          origem_nome: nome,
        });
      });

      onChange(newItems);

      // Check passive monitors
      const usandoPassivos = names.filter((nome) => {
        const av = avaliacoes.find((x) => x.nome === nome);
        if (!av) return false;
        return (av.coletor_primario || "").startsWith("Mon");
      });
      setSubsetPassivo(usandoPassivos);
      if (usandoPassivos.length > 0) {
        toast.warning(
          `${usandoPassivos.length} avaliação(ões) usam monitor passivo — o custo do monitor é cobrado separadamente pelo laboratório.`,
          { duration: 7000 }
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao otimizar");
    } finally {
      setOptimizing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, pacotes, avaliacoes, markup, transporte, forceNonOptimized, onChange]);

  // Re-run optimize whenever selection changes, or mode toggles.
  useEffect(() => {
    optimize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, forceNonOptimized, pacotes.length, avaliacoes.length]);

  const selectedArr = Array.from(selected);

  return (
    <div className="grid md:grid-cols-[1fr_380px] gap-4">
      {/* ---- Picker ---- */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedArr.length} selecionada(s) de {avaliacoesAtivas.length}</span>
            {selectedArr.length > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={clearAll} data-testid="chem-clear-all">
                <X className="w-3 h-3 mr-1" /> Limpar
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin ? (
              <>
                <Label htmlFor="force-non-opt" className="text-xs text-muted-foreground cursor-pointer">Forçar modo não-otimizado</Label>
                <Switch
                  id="force-non-opt"
                  checked={!!forceNonOptimized}
                  onCheckedChange={(v) => onToggleForce && onToggleForce(v)}
                  data-testid="chem-force-non-optimized"
                />
              </>
            ) : (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="chem-opt-locked">
                <Lock className="w-3 h-3" /> Otimização sempre ativa
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar avaliação por nome ou CAS..."
            className="pl-9"
            data-testid="chem-picker-search"
          />
        </div>

        <div className="border border-border rounded-lg overflow-auto max-h-[380px]" data-testid="chem-picker-list">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              Nenhuma avaliação encontrada.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((a) => {
                const isSel = selected.has(a.nome);
                const sub = a.custo_analise_laboratorial_brl == null;
                const passivo = (a.coletor_primario || "").startsWith("Mon");
                return (
                  <li
                    key={a.nome}
                    onClick={() => toggleSelected(a.nome)}
                    className={`px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-muted/30 ${isSel ? "bg-primary/10" : ""}`}
                    data-testid={`chem-pick-${a.nome}`}
                  >
                    <input type="checkbox" readOnly checked={isSel} className="pointer-events-none" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        {a.nome}
                        {passivo && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/40 text-amber-600 dark:text-amber-400">
                            monitor passivo
                          </Badge>
                        )}
                        {sub && <Badge variant="outline" className="text-[9px] h-4 px-1">subcontratado</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono flex gap-3">
                        <span>{a.cas || "—"}</span>
                        <span>{a.coletor_primario || "—"}</span>
                        {!sub && isAdmin && <span>Lab: {formatBRL(a.custo_analise_laboratorial_brl)}</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {selectedArr.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedArr.map((n) => (
              <Badge
                key={n}
                variant="secondary"
                className="cursor-pointer"
                onClick={() => toggleSelected(n)}
                data-testid={`chem-chip-${n}`}
              >
                {n} <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* ---- Result panel ---- */}
      <aside className="rounded-lg border border-border bg-card p-4 space-y-3 h-fit sticky top-4" data-testid="chem-optimizer-result">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <div className="font-medium text-sm">
            {forceNonOptimized ? "Somatório individual" : "Combinação otimizada"}
          </div>
          {optimizing && <span className="text-xs text-muted-foreground">calculando...</span>}
        </div>

        {!result || selectedArr.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            Selecione pelo menos uma avaliação para ver a combinação ótima de pacotes e individuais.
          </div>
        ) : (
          <>
            {isAdmin && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Custo total</div>
                  <div className="font-mono font-semibold text-primary" data-testid="chem-result-custo">
                    {formatBRL(result.custo_total_brl)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Soma individual</div>
                  <div className="font-mono text-muted-foreground line-through text-xs">
                    {formatBRL(result.custo_sem_otimizacao_brl)}
                  </div>
                </div>
              </div>
            )}

            {isAdmin && result.economia_brl > 0 && (
              <div className="rounded border border-emerald-500/20 bg-emerald-500/5 p-2" data-testid="chem-result-economia">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  Economia com otimização
                </div>
                <div className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                  {formatBRL(result.economia_brl)} ({formatPct(result.economia_pct)})
                </div>
              </div>
            )}

            {(result.pacotes_aplicados || []).length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1 text-xs font-medium">
                  <Package className="w-3 h-3" /> Pacotes aplicados
                </div>
                {result.pacotes_aplicados.map((p) => (
                  <div key={p.nome} className="text-xs rounded border border-border/60 p-2" data-testid={`chem-result-pkg-${p.nome}`}>
                    <div className="font-medium flex items-center justify-between">
                      <span>{p.nome}</span>
                      {isAdmin && <span className="font-mono">{formatBRL(p.custo_brl)}</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Cobre {(p.cobre || []).length}: {(p.cobre || []).join(", ")}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(result.individuais_aplicados || []).length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium">
                  <FlaskConical className="w-3 h-3" /> Individuais
                </div>
                <ul className="text-[11px] space-y-0.5">
                  {result.individuais_aplicados.map((i) => (
                    <li key={i.nome} className="flex justify-between gap-2">
                      <span className="truncate">{i.nome}</span>
                      {isAdmin && <span className="font-mono text-muted-foreground">{formatBRL(i.custo_brl)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(result.subcontratados || []).length > 0 && (
              <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs" data-testid="chem-result-subcontratados">
                <div className="flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-3 h-3" /> Subcontratadas ({result.subcontratados.length})
                </div>
                <div className="text-[10px] mt-1 text-amber-800 dark:text-amber-300">
                  Sem preço de laboratório — cotar manualmente: {result.subcontratados.join(", ")}
                </div>
              </div>
            )}

            {subsetPasivo.length > 0 && (
              <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs" data-testid="chem-result-passivos">
                <div className="flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400">
                  <Info className="w-3 h-3" /> Monitores passivos
                </div>
                <div className="text-[10px] mt-1 text-amber-800 dark:text-amber-300">
                  {subsetPasivo.length} avaliação(ões) com coletor passivo. O monitor é cobrado à parte pelo laboratório e não está incluso no custo acima.
                </div>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
